import { arrayToHash } from '@ali/re-utils';
import { EventEmitter } from 'events';

export default class Form {
  constructor() {
    this.emitter = new EventEmitter();
    this.items = [];
  }

  getData(forceValid = true) {
    if (forceValid) {
      this.forceValid();
    }
    return this.getFormData();
  }

  getFieldData(forceValid = true) {
    if (forceValid) {
      this.forceValid();
    }
    return this.getSimpleFormData();
  }

  getValue(forceValid = true) {
    if (forceValid) {
      this.forceValid();
    }
    const errorMsg = [];
    const content = {};

    this.items.forEach((state) => {
      const data = state.getAsFormData('value');
      if (data.hasError) {
        errorMsg.push(data.errorMsg);
      }
      content[data.fieldName || data.fieldId] = (data.fieldData || {}).value;
    });

    return {
      success: errorMsg.length < 1,
      content,
      errorMsg,
    };
  }

  setFieldData(fieldData) {
    this.items.forEach((state) => {
      const fieldId = state.getValue('fieldId');
      const fieldName = state.getValue('fieldName');
      const current = fieldData[fieldName] !== undefined
        ? fieldData[fieldName] : fieldData[fieldId];
      if (current !== undefined) {
        const data = {
          fieldData: current,
          fieldId,
          fieldName,
        };
        const dataKey = state.getValue('$dataKey');
        // 对迭代器的处理
        if (dataKey) {
          data[dataKey] = this.getRawData(state, data.fieldData);
          data.$dataKey = dataKey;
        }
        state.setAsFormData(data);
      }
    });
  }

  setValue(value) {
    this.items.forEach((state) => {
      const fieldId = state.getValue('fieldId');
      const fieldName = state.getValue('fieldName');
      const current = value[fieldName] !== undefined ? value[fieldName] : value[fieldId];
      if (current !== undefined) {
        const data = {
          fieldData: { value: current },
          fieldId,
          fieldName,
        };
        const dataKey = state.getValue('$dataKey');
        // 对迭代器的处理
        if (dataKey) {
          data[dataKey] = this.getRawData(state, data.fieldData);
          data.$dataKey = dataKey;
        }
        state.setAsFormData(data);
      }
    });
  }

  setData(data) {
    let formData = data;
    if (Array.isArray(data)) {
      formData = {};
      data.forEach((d) => {
        const key = d.fieldName || d.fieldId;
        if (key) {
          formData[key] = d;
        }
      });
    }
    this.items.forEach((state) => {
      const fieldId = state.getValue('fieldId');
      const fieldName = state.getValue('fieldName');
      const currentData = formData[fieldName] || formData[fieldId];
      if (currentData) {
        const dataKey = state.getValue('$dataKey');
        // 对迭代器的处理
        if (dataKey) {
          currentData[dataKey] = this.getRawData(state, currentData.fieldData);
          currentData.$dataKey = dataKey;
        }
        state.setAsFormData(currentData);
      }
    });
    return true;
  }

  getRawData(state, fieldData = {}) {
    const dataKey = state.getValue('$dataKey');
    const $data = state.getValue(dataKey);
    if (!Array.isArray(fieldData.value)) {
      return $data;
    }
    $data.forEach((d, i) => {
      const $fieldData = Array.isArray(fieldData.value[i])
        ? arrayToHash(fieldData.value[i], 'fieldId')
        : fieldData.value[i];
      if ($fieldData) {
        Object.keys(d.$fieldData).forEach((key) => {
          if ($fieldData[key]) {
            d.$fieldData[key].fieldData = $fieldData[key].fieldData || $fieldData[key];
          }
        });
      }
    });
    return $data;
  }

  setBehavior(val) {
    this.items.forEach(state => state.setBehavior(val));
  }

  getFormData() {
    const errorMsg = [];

    const content = this.items.map((state) => {
      const data = state.getAsFormData();
      if (data.hasError) {
        errorMsg.push(data.errorMsg);
      }
      return data;
    });

    return {
      success: errorMsg.length < 1,
      content,
      errorMsg,
    };
  }

  setFormData(formData) {
    this.items.forEach((state) => {
      const fieldId = state.getValue('fieldId');
      const fieldName = state.getValue('fieldName');
      const data = formData !== undefined ? formData[fieldName] : formData[fieldId];
      if (data !== undefined) {
        state.mergeData(data);
      }
    });
  }

  getSimpleFormData() {
    const errorMsg = [];
    const content = {};

    this.items.forEach((state) => {
      const data = state.getAsFormData(true);
      if (data.hasError) {
        errorMsg.push(data.errorMsg);
      }
      content[data.fieldName || data.fieldId] = data.fieldData;
    });

    return {
      success: errorMsg.length < 1,
      content,
      errorMsg,
    };
  }

  getItems() {
    return this.items;
  }

  add(state) {
    this.items.push(state);
    // todo: need?
    state.onPurge(() => this.remove(state));
    state.setOwnerForm(this);
  }

  remove(state) {
    const i = this.items.indexOf(state);
    if (i > -1) {
      this.items.splice(i, 1);
    }
  }

  forceValid() {
    return this.items.map(state => state.forceValid()).filter(item => item && item.hasError);
  }

  reset(keys) {
    this.items.forEach(state => state.reset(keys));
    this.emitReset(this.items);
  }

  saveResetPoint() {
    this.items.forEach(state => state.saveResetPoint());
  }

  // todo
  // clearValue() {
  //   this.items.forEach(state => state.clearValue());
  // }

  /**
   * 监听表单 change
   */
  onChange(fn) {
    this.emitter.on('change', fn);
    return () => {
      this.emitter.removeListener('change', fn);
    };
  }

  /**
   * 监听表单 reset
   * @param fn
   * @return {Function}
   */
  onReset(fn) {
    this.emitter.on('reset', fn);
    return () => {
      this.emitter.removeListener('reset', fn);
    };
  }

  /**
   * 触发表单 change
  * @param targetKey
   * @param data
   */
  emitChange(data, targetKey) {
    this.emitter.emit('change', data, targetKey);
  }

  /**
   * 触发表单 change
   * @param data
   */
  emitReset(data) {
    this.emitter.emit('reset', data);
  }

  /**
   * 获取form下的item
   * @param {string} fieldId
   */
  getItem(fieldId) {
    return this.getItems().filter(item => item.getData('fieldId') === fieldId)[0];
  }
}
